import React from 'react';
import {
  getImageUrlsFromNames,
  getImageUrlWithFallback,
  debugS3Configuration,
  resolveStorageUrl,
} from '../../frontend/Customer/aws/s3FileUpload';

type ReviewImageUrlLike =
  | string
  | {
      url?: string;
      fullUrl?: string;
      imageUrl?: string;
      src?: string;
      key?: string;
      imageName?: string;
      name?: string;
    };

interface ReviewImagesProps {
  imageNames?: ReviewImageUrlLike[];
  imageUrls?: ReviewImageUrlLike[];
  maxDisplay?: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  showCount?: boolean;
  folderName?: string;
  layout?: 'row' | 'column' | 'grid-2' | 'grid-3' | 'grid-4';
  enablePreview?: boolean;
  onImageClick?: (images: string[], index: number) => void;
}

const ReviewImages: React.FC<ReviewImagesProps> = ({
  imageNames = [],
  imageUrls = [],
  maxDisplay = 3,
  size = 'md',
  className = '',
  showCount = true,
  folderName = 'review-images',
  layout = 'row',
  enablePreview = false,
  onImageClick,
}) => {
  // State to track failed images
  const [failedImages, setFailedImages] = React.useState<Set<string>>(
    new Set()
  );

  const extractString = React.useCallback(
    (value: ReviewImageUrlLike): string => {
      if (typeof value === 'string') {
        return value;
      }

      if (!value || typeof value !== 'object') {
        return '';
      }

      const candidate =
        value.fullUrl ||
        value.url ||
        value.imageUrl ||
        value.src ||
        value.key ||
        value.imageName ||
        value.name;

      return typeof candidate === 'string' ? candidate : '';
    },
    []
  );

  // State to track loading images
  const [loadingImages, setLoadingImages] = React.useState<Set<string>>(
    new Set()
  );

  // Determine which images to display - use URLs directly from payload
  const displayImages = React.useMemo(() => {
    if (imageUrls.length > 0) {
      const validUrls = imageUrls
        .map((value) => extractString(value))
        .filter(
          (url): url is string => typeof url === 'string' && url.trim() !== ''
        )
        // Normalize via resolveStorageUrl so S3 URLs from the backend can be rewritten to CDN.
        // If the backend sent keys/relative paths, resolveStorageUrl(folder) will also handle those.
        .map((url) => {
          const trimmed = url.trim();
          return resolveStorageUrl(trimmed, folderName) || trimmed;
        })
        .filter(
          (url): url is string => typeof url === 'string' && url.trim() !== ''
        );

      console.log('ReviewImages: Using imageUrls from payload:', {
        originalUrls: imageUrls,
        validUrls: validUrls.length,
      });

      return validUrls;
    }

    // Fallback to imageNames if imageUrls is empty
    if (imageNames.length > 0) {
      // Convert image names to URLs only if imageUrls is not available
      const normalizedNames = imageNames
        .map((value) => extractString(value).trim())
        .filter(
          (name): name is string => typeof name === 'string' && name !== ''
        );

      const urls = getImageUrlsFromNames(normalizedNames, folderName);
      const validUrls = urls.filter(
        (url): url is string => typeof url === 'string' && url.trim() !== ''
      );

      console.log('ReviewImages: Converting image names to URLs:', {
        imageNames: normalizedNames,
        folderName,
        generatedUrls: urls,
        validUrls: validUrls.length,
      });

      return validUrls;
    }

    console.log('ReviewImages: No images to display');
    return [];
  }, [extractString, imageNames, imageUrls, folderName]);

  // Get size classes with responsive design
  const sizeClasses = {
    sm: 'w-12 h-12 sm:w-14 sm:h-14',
    md: 'w-16 h-16 sm:w-20 sm:h-20',
    lg: 'w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36',
    xl: 'w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48',
  };

  const imageSize = sizeClasses[size];
  const totalImages = displayImages.length;

  // Don't render if no images
  if (displayImages.length === 0) {
    console.log('ReviewImages: No images to display, returning null');
    return null;
  }

  // Determine layout classes with tighter, content-sized grid columns to avoid large empty gaps
  const layoutClasses =
    layout === 'column'
      ? 'flex flex-col gap-2'
      : layout === 'grid-2'
        ? 'grid grid-cols-2 gap-2 w-fit'
        : layout === 'grid-3'
          ? 'grid grid-cols-2 sm:grid-cols-3 gap-2 w-fit'
          : layout === 'grid-4'
            ? 'flex flex-wrap gap-2 laptop:grid laptop:grid-cols-6 laptop:gap-2 laptop:w-fit'
            : 'flex flex-wrap gap-2';

  return (
    <>
      <div className={`${layoutClasses} ${className}`}>
        {/* Display images up to maxDisplay limit */}
        {displayImages
          .slice(0, maxDisplay)
          .map((imageUrl: string, idx: number) => (
            <div
              key={idx}
              className={`relative group flex items-center justify-center flex-none ${enablePreview ? 'cursor-pointer' : ''}`}
              onClick={() => {
                if (!enablePreview) return;
                console.log(
                  'ReviewImages: Image clicked, calling onImageClick with all images',
                  { totalImages: displayImages.length, clickedIndex: idx }
                );
                // Pass ALL images, not just the displayed ones, so user can scroll through all
                onImageClick?.(displayImages, idx);
              }}
              role="button"
              tabIndex={enablePreview ? 0 : -1}
              onKeyDown={(e) => {
                if (!enablePreview || (e.key !== 'Enter' && e.key !== ' '))
                  return;
                e.preventDefault();
                onImageClick?.(displayImages, idx);
              }}
            >
              {/* Loading indicator */}
              {loadingImages.has(imageUrl) && (
                <div
                  className={`${imageSize} absolute inset-0 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center`}
                >
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                </div>
              )}

              <img
                src={imageUrl}
                alt={`Review image ${idx + 1}`}
                className={`${imageSize} flex-none object-cover rounded-lg border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 ${loadingImages.has(imageUrl) ? 'opacity-0' : 'opacity-100'} ${failedImages.has(imageUrl) ? 'opacity-50' : ''}`}
                onLoadStart={() => {
                  setLoadingImages((prev) => {
                    const next = new Set(prev);
                    next.add(imageUrl);
                    return next;
                  });
                  setFailedImages((prev) => {
                    const next = new Set(prev);
                    next.delete(imageUrl);
                    return next;
                  });
                }}
                onLoad={() => {
                  setLoadingImages((prev) => {
                    const next = new Set(prev);
                    next.delete(imageUrl);
                    return next;
                  });
                  setFailedImages((prev) => {
                    const next = new Set(prev);
                    next.delete(imageUrl);
                    return next;
                  });
                }}
                onError={(e) => {
                  // Enhanced error handling for broken images
                  const target = e.target as HTMLImageElement;
                  const errorDetails = {
                    imageUrl,
                    imageIndex: idx,
                    imageName: imageNames[idx] || 'N/A',
                    folderName,
                    originalSrc: target.src,
                    timestamp: new Date().toISOString(),
                  };

                  console.error(
                    'ReviewImages: Failed to load image:',
                    errorDetails
                  );

                  // Track failed image and clear loading state
                  setFailedImages((prev) => {
                    const next = new Set(prev);
                    next.add(imageUrl);
                    return next;
                  });
                  setLoadingImages((prev) => {
                    const next = new Set(prev);
                    next.delete(imageUrl);
                    return next;
                  });

                  // Run configuration debug on first error
                  if (idx === 0) {
                    console.log(
                      'Running configuration debug due to image load failure...'
                    );
                    debugS3Configuration();
                  }

                  // Prevent infinite error loops
                  if (
                    target.src.includes('data:image/svg+xml') ||
                    target.src.includes('placeholder') ||
                    target.src.includes('fallback-attempted') ||
                    target.src.includes('folder-fallback-attempted') ||
                    target.src.includes('via.placeholder.com')
                  ) {
                    return;
                  }

                  // Try S3 direct URL fallback if we have an image name and current URL is CDN
                  const imageName = imageNames[idx]
                    ? extractString(imageNames[idx]).trim()
                    : '';
                  if (imageName) {
                    const fallbackUrl =
                      resolveStorageUrl(imageName, folderName) ||
                      getImageUrlWithFallback(imageName, folderName);
                    if (fallbackUrl && fallbackUrl !== imageUrl) {
                      console.log(
                        'ReviewImages: Retrying image with normalized URL:',
                        {
                          originalUrl: imageUrl,
                          fallbackUrl,
                          imageName,
                          reason: 'cd_or_s3_retry',
                        }
                      );
                      target.src =
                        fallbackUrl +
                        (fallbackUrl.includes('?') ? '&' : '?') +
                        'fallback-attempted=true';
                      return;
                    }
                  }

                  // Try alternative folder names if the current one fails
                  if (
                    imageName &&
                    target.src.includes('amazonaws.com') &&
                    !target.src.includes('fallback-attempted')
                  ) {
                    const alternativeFolders = ['review-images', 'reviews'];
                    const currentFolder = folderName;
                    const alternativeFolder = alternativeFolders.find(
                      (folder) => folder !== currentFolder
                    );

                    if (alternativeFolder) {
                      const alternativeUrl = getImageUrlWithFallback(
                        imageName,
                        alternativeFolder
                      );
                      if (alternativeUrl && alternativeUrl !== imageUrl) {
                        console.log(
                          'ReviewImages: Trying alternative folder:',
                          {
                            originalUrl: imageUrl,
                            alternativeUrl,
                            imageName,
                            currentFolder,
                            alternativeFolder,
                            reason: 'folder_mismatch_fallback',
                          }
                        );
                        target.src =
                          alternativeUrl + '?folder-fallback-attempted=true';
                        return;
                      }
                    }
                  }

                  // Create a simple SVG placeholder as final fallback
                  const svgPlaceholder = `data:image/svg+xml;base64,${btoa(`
                  <svg width="150" height="150" xmlns="http://www.w3.org/2000/svg">
                    <rect width="150" height="150" fill="#f3f4f6" stroke="#e5e7eb" stroke-width="1"/>
                    <circle cx="75" cy="60" r="15" fill="#d1d5db"/>
                    <rect x="50" y="85" width="50" height="30" rx="4" fill="#d1d5db"/>
                    <text x="75" y="130" font-family="Arial, sans-serif" font-size="12" fill="#9ca3af" text-anchor="middle">Image</text>
                    <text x="75" y="145" font-family="Arial, sans-serif" font-size="12" fill="#9ca3af" text-anchor="middle">Not Found</text>
                  </svg>
                `)}`;

                  console.log('Using SVG placeholder as final fallback');
                  target.src = svgPlaceholder;
                }}
              />
            </div>
          ))}

        {/* Show count indicator if there are more images */}
        {showCount && totalImages > maxDisplay && (
          <div
            className={`${imageSize} bg-gray-100 rounded-lg border border-gray-200 flex flex-col items-center justify-center text-xs text-gray-600 font-medium`}
            title={`${totalImages} images`}
          >
            <div className="text-lg font-bold text-gray-700">
              +{totalImages - maxDisplay}
            </div>
            <div className="text-[10px] text-gray-500">more</div>
          </div>
        )}
      </div>
    </>
  );
};

export default ReviewImages;

// Hook for managing review images
export const useReviewImages = (
  imageNames: ReviewImageUrlLike[],
  imageUrls: ReviewImageUrlLike[],
  folderName = 'review-images'
) => {
  const displayUrls = React.useMemo(() => {
    const normalizedUrls = (Array.isArray(imageUrls) ? imageUrls : [])
      .map((value) =>
        typeof value === 'string'
          ? value
          : ((value?.fullUrl ||
              value?.url ||
              value?.imageUrl ||
              value?.src ||
              value?.key ||
              value?.imageName ||
              value?.name) ??
            '')
      )
      .filter(
        (value): value is string =>
          typeof value === 'string' && value.trim() !== ''
      )
      .map((value) => resolveStorageUrl(value.trim(), folderName));

    if (normalizedUrls.length > 0) {
      return normalizedUrls;
    }

    const normalizedNames = (Array.isArray(imageNames) ? imageNames : [])
      .map((value) =>
        typeof value === 'string'
          ? value
          : ((value?.imageName ||
              value?.name ||
              value?.key ||
              value?.url ||
              value?.fullUrl ||
              value?.imageUrl ||
              value?.src) ??
            '')
      )
      .filter(
        (value): value is string =>
          typeof value === 'string' && value.trim() !== ''
      );

    if (normalizedNames.length > 0) {
      return getImageUrlsFromNames(normalizedNames, folderName);
    }

    return [];
  }, [imageNames, imageUrls, folderName]);

  const hasImages =
    (imageNames?.length || 0) > 0 || (imageUrls?.length || 0) > 0;
  const totalCount =
    (imageNames?.length || 0) > 0 ? imageNames.length : imageUrls.length;

  return {
    displayUrls,
    hasImages,
    totalCount,
    imageNames,
    imageUrls,
  };
};
